/**
 * Central registry of help docs for the ClickHouse orchestration UI.
 *
 * Every operator action in ClickHouseNodesPage points at one of these entries
 * via <HelpIcon docKey="..."/>. Keeping them in one file means the docs never
 * drift from the UI and can be maintained without hunting through JSX.
 *
 * Each entry's `body` is rendered as Markdown-lite (paragraphs + bullets +
 * inline `code`). Keep them concise — the modal is meant to answer "what
 * does this button do, when do I use it, what breaks if I misuse it" in
 * under a minute of reading.
 */

export const CLICKHOUSE_HELP = {
  // ─────────────────────── Top-level page ───────────────────────
  page_overview: {
    title: 'ClickHouse Orchestration',
    body: [
      'This page orchestrates the ClickHouse fleet that stores every tenant\'s observability data — logs, spans, metrics, RUM events.',
      '',
      '**Three tabs:**',
      '- **Nodes & Binaries** — individual ClickHouse servers (SSH-managed) and the binary artifacts you deploy to them.',
      '- **Clusters** — logical groupings of nodes with a shard/replica topology. A cluster is the unit that tenants are routed to.',
      '- **Tenant Routing** — which organization writes/reads from which cluster.',
      '',
      '**Typical workflow for a new cluster:**',
      '1. Upload a ClickHouse binary once (Nodes & Binaries).',
      '2. Add the SSH-reachable hosts as nodes.',
      '3. Create a cluster with a topology (e.g. 2-shard × 2-replica).',
      '4. Assign nodes to shard/replica slots.',
      '5. Deploy the binary to the cluster. Start.',
      '6. Assign tenants in Tenant Routing.',
      '',
      '**Data-safety reminder:** deleting a cluster or unassigning a tenant disconnects live write paths. Always verify on the Data Explorer page that reads still work before considering a change complete.',
    ],
  },

  // ─────────────────────── Nodes & Binaries tab ───────────────────────
  nodes_tab: {
    title: 'Nodes & Binaries',
    body: [
      'A **node** is a single Linux host we reach by SSH to install, configure, start, and monitor a ClickHouse server process.',
      '',
      'A **binary** is a ClickHouse release artifact (`.tgz`, `.deb`, `.rpm`, or a raw executable). Upload once, deploy to many.',
      '',
      'Adding a node here does NOT deploy anything. It just records the SSH credentials and resource profile. You then run **Deploy Binary** to install ClickHouse on it.',
    ],
  },

  add_node: {
    title: 'Add Node',
    body: [
      'Registers a new ClickHouse host in the orchestrator. No SSH command is run at this step — we only test that we can reach the host later when you run **Test SSH** or **Pre-flight Check**.',
      '',
      '**Required:**',
      '- `host` — IP or DNS name reachable from the backend',
      '- `sshUsername` — account that can `sudo` (needed for `systemctl` and writing `/etc/clickhouse-server/`)',
      '- Either `sshPassword` OR `sshPrivateKey`',
      '',
      '**Port defaults:** HTTP `8123`, TCP `9000`, Keeper `9181` — only change if those ports clash with something else on the host.',
      '',
      '`dataDir` is where ClickHouse will store its data partitions. Default `/var/lib/clickhouse` works on stock Debian/Ubuntu; change only if you\'re pinning to a separate data volume.',
    ],
  },

  upload_binary: {
    title: 'Upload Binary',
    body: [
      'Stores a ClickHouse release artifact on the backend so it can later be SFTP\'d to any node during a **Deploy**.',
      '',
      '**Accepted formats:**',
      '- `.tgz` / `.tar.gz` — official compressed release archive',
      '- `.deb`, `.rpm` — distribution packages',
      '- raw executable (rarely used — only if you have a custom build)',
      '',
      '**Where to get one:** `https://github.com/ClickHouse/ClickHouse/releases` or `https://packages.clickhouse.com/tgz/stable/`.',
      '',
      'The backend stores the file under `data/clickhouse-binaries/`. Binaries are not deleted automatically — clean up old versions manually to reclaim disk.',
      '',
      '⚠ We do NOT currently verify signatures or checksums of uploaded binaries. Only upload binaries from a trusted source.',
    ],
  },

  preflight_check: {
    title: 'Pre-flight Check',
    body: [
      'Runs a read-only inspection over SSH to verify the host is ready for a ClickHouse install — BEFORE you actually deploy.',
      '',
      '**What it checks:**',
      '- RAM (≥ 4 GB recommended)',
      '- Free disk space under `dataDir`',
      '- Required ports (`8123`, `9000`, optionally `9181`) are free',
      '- File-descriptor limit (`ulimit -n` should be ≥ 262144)',
      '- Whether ClickHouse is already installed on the host',
      '- Basic OS compatibility',
      '',
      '**When to run:** after adding a node and before your first **Deploy**. Re-run any time you change hardware or OS limits on the host.',
      '',
      'Pre-flight is read-only — it never modifies the remote host.',
    ],
  },

  test_ssh: {
    title: 'Test SSH',
    body: [
      'Attempts a single SSH login using the credentials stored for this node and runs `uname -a`.',
      '',
      'Use this to confirm the SSH password/key still works after a server rebuild, password rotation, or moving the node to a new network. It does not inspect ClickHouse state at all.',
    ],
  },

  health_check: {
    title: 'Health Check',
    body: [
      'Makes an HTTP request to the node\'s ClickHouse port and runs `SELECT 1`. Fast (~100ms).',
      '',
      'This tells you the ClickHouse process is alive and accepting queries. It does NOT check replication lag, disk usage, or merge health — use **Deep Health** for that.',
      '',
      'The periodic health check in the backend uses this same probe; the button here is for on-demand verification.',
    ],
  },

  deep_health: {
    title: 'Deep Health Check',
    body: [
      'Runs a battery of diagnostic queries against the node to surface issues a basic ping would miss.',
      '',
      '**Queries executed:**',
      '- `system.tables` — enumerates schema; confirms the `verops` database is initialized',
      '- `system.databases` — sanity check for database presence',
      '- `system.replication_queue` — detects stuck replication (relevant only on ReplicatedMergeTree)',
      '- `system.merges` — any in-progress or stuck merges',
      '- `system.parts` — counts active vs inactive parts',
      '',
      'Results render in a table below the card. A "degraded" verdict means the node is alive but one of the checks returned something non-trivial (non-empty replication queue, too many inactive parts, etc.).',
    ],
  },

  deploy_binary: {
    title: 'Deploy Binary',
    body: [
      'Runs the full install pipeline on this node:',
      '1. SFTP the selected binary to `/tmp/verops_deploy_<filename>`',
      '2. Stop existing ClickHouse (if any), clean stale decompression locks',
      '3. Extract/install (binary-specific path per format)',
      '4. Render `config.xml` + `users.xml` if not already present',
      '5. Install and enable a `clickhouse-server.service` systemd unit',
      '6. Start the service and wait for the HTTP port to answer (up to 40 s)',
      '7. Run a health check',
      '',
      '**Side effects:** ClickHouse stops briefly during step 2. If this node serves live traffic, expect a short write/read outage. See **Graceful Drain** (coming) for a zero-loss path.',
      '',
      'Deployment progress streams into the **View Logs** dialog in real-time.',
    ],
  },

  start_node: {
    title: 'Start Node',
    body: [
      'Runs `systemctl start clickhouse-server` on the node and waits for the HTTP port to answer.',
      '',
      'Use after a **Stop** or after a host reboot if the service failed to auto-start (it\'s set to enabled, so reboots normally bring it up automatically).',
    ],
  },

  stop_node: {
    title: 'Stop Node',
    body: [
      'Runs `systemctl stop clickhouse-server` on the node.',
      '',
      '⚠ **This is not a drain.** In-flight writes to this node may be lost if they were buffered and not yet flushed to disk. For zero-loss cluster-wide restarts, use the cluster-level **Rolling Restart** (coming).',
      '',
      'The node remains registered in the orchestrator — this only stops the process.',
    ],
  },

  restart_node: {
    title: 'Restart Node',
    body: [
      'Equivalent to **Stop** followed by **Start**. Used after config changes that require a ClickHouse restart (e.g., most changes to `config.xml`).',
      '',
      '⚠ Same write-loss caveat as Stop.',
    ],
  },

  view_logs: {
    title: 'View Deployment Logs',
    body: [
      'Streams the deployment log for this node in real-time over WebSocket.',
      '',
      'This is the log produced by the orchestrator (each step of the deploy/start/stop pipeline, with timestamps and exit codes). It is NOT the ClickHouse process log — for that, SSH into the host and tail `/var/log/clickhouse-server/clickhouse-server.log`.',
    ],
  },

  delete_node: {
    title: 'Delete Node',
    body: [
      '**Destructive.** Removes the node from the orchestrator.',
      '',
      'If you check **"Also remove ClickHouse from remote server"**, the orchestrator will SSH in and:',
      '- Stop and disable the systemd unit',
      '- Remove `/etc/clickhouse-server/` and `/usr/bin/clickhouse`',
      '- **Leave `/var/lib/clickhouse/` (your data) intact** — you must remove it manually if desired',
      '',
      'You cannot delete a node that is still assigned to a cluster. Remove the cluster assignment first.',
    ],
  },

  node_actions_legend: {
    title: 'Node actions — what each icon does',
    body: [
      'Each icon in the Actions column maps to a specific SSH or HTTP operation on the node. Destructive actions are listed last.',
      '',
      '- **Pre-flight Check** — read-only SSH inspection of RAM, disk, ports, fd limits, existing install. Safe to run anytime.',
      '- **Test SSH** — `uname -a` login check. Confirms SSH creds are still valid.',
      '- **Health Check** — HTTP `SELECT 1`. Fast liveness probe.',
      '- **Deep Health** — runs a battery of `system.*` queries to surface replication lag, stuck merges, zombie parts.',
      '- **Disk budget (Dns icon)** — set or clear the node\'s `keep_free_space_bytes` reservation. ClickHouse refuses INSERTs when usage crosses the budget. See **Disk budget** help for the full model.',
      '- **Deploy** — full install pipeline: SFTP binary → stop existing → install → render configs → systemd enable → start → wait for HTTP.',
      '- **Start** — `systemctl start clickhouse-server` + wait for HTTP port.',
      '- **Stop** — `systemctl stop clickhouse-server`. ⚠ Not a drain — in-flight writes may be lost.',
      '- **Restart** — Stop then Start. ⚠ Same write-loss caveat as Stop.',
      '- **Logs** — streams the orchestrator\'s step-by-step deploy log over WebSocket. Not the ClickHouse process log.',
      '- **Delete** — removes the node from the orchestrator. Optionally scrubs ClickHouse from the remote host (leaves `/var/lib/clickhouse` intact).',
    ],
  },

  disk_budget: {
    title: 'Disk budget (keep_free_space_bytes)',
    body: [
      'Caps how much disk space ClickHouse is allowed to use on this node, independent of the filesystem size. When usage crosses the budget, ClickHouse **refuses new INSERTs** — existing data stays readable, merges keep running, replication keeps working.',
      '',
      '**What the orchestrator writes:** `/etc/clickhouse-server/config.d/verops-disk-budget.xml` with a single `<keep_free_space_bytes>` value equal to `(diskTotalGb - budgetGb) × 1 GiB`. We call `SYSTEM RELOAD CONFIG` after writing so the change applies without a process restart — but if that reload fails (for example, because your cluster config references Keeper and Keeper isn\'t up yet), the value still persists on disk and is picked up on next **Start / Restart**. Check `SELECT name, keep_free_space FROM system.disks` to confirm the value ClickHouse is actively enforcing.',
      '',
      '**Example:** 225 GB disk, budget 200 GB → `keep_free_space_bytes = 25 GiB`. ClickHouse will refuse INSERTs once the filesystem has less than 25 GiB free (≈ when used data reaches 200 GB).',
      '',
      '**Why bother** when the filesystem already has a natural limit:',
      '- Leaves headroom for merges. A merge can temporarily use ~2× the largest part — if the filesystem fills while a merge is running, that merge AND any other in-flight operation can fail.',
      '- Lets you plan capacity. Setting an explicit budget forces the conversation "how big do we let this get before scaling out" instead of discovering it at 100% disk.',
      '- Keeps the **other processes** on the node healthy. A shared box running ClickHouse + agents + systemd logs benefits from leaving ClickHouse a hard ceiling.',
      '',
      '**What this does NOT do:**',
      '- It does not delete data. Use table **TTL** (currently 365 d) or the per-tenant DELETE mutation (deferred feature) to shrink existing data.',
      '- It does not prevent replication from pulling parts — a replica still pulls whatever the primary has, so if the budget is tighter than a sibling replica the secondary will eventually fail replication. Set budgets identically across replicas of a shard.',
      '- It does not rebalance. Once full, ClickHouse stops accepting writes on THIS node. If you have multi-shard, the other shards keep taking traffic via the distributed engine; on single-shard the cluster stops ingesting entirely until you raise the budget or free space.',
      '',
      '**Clearing the budget:** removes the config file and reloads. ClickHouse goes back to using the full disk.',
      '',
      '**How warnings work:** the budget itself is informative — you can declare a budget larger than the physical disk and the orchestrator will silently clamp it at save time (no warning, no consistency finding). The warnings you\'ll actually see are **utilization-based**: the Operational Health banner and the disk-low chip on each node row fire WARNING at 15% / 20 GB free-against-the-budget and CRITICAL at 5% / 10 GB. The "ceiling" is whichever is stricter — the budget (if set) or the total disk — so utilization is always measured against the real wall you\'ll hit first.',
    ],
  },

  cluster_actions_legend: {
    title: 'Cluster actions — what each icon does',
    body: [
      '- **View Nodes** — list the nodes assigned to this cluster with their shard/replica slots.',
      '- **Assign Node** — place a node into a specific shard+replica slot.',
      '- **Configure** — regenerate and push `remote_servers.xml`, `macros.xml`, `use_keeper.xml`, `keeper.xml` to every node. Restart required for new config to take effect.',
      '- **Deploy** — run **Deploy** on every node in the cluster in parallel. Full outage.',
      '- **Start** — KEEPER nodes first (quorum forms), DATA nodes second.',
      '- **Graceful Stop** — drains merges (SYSTEM STOP MERGES, waits for system.merges to empty, SYSTEM FLUSH LOGS), then stops. Preferred for scheduled maintenance.',
      '- **Hard Stop** — immediate shutdown. ⚠ In-flight writes and merges may be lost.',
      '- **Health Summary** — aggregate of per-node health checks (fast).',
      '- **Health Detail** — full operational dashboard: disk usage per table, active merges, slow queries, in-flight mutations, replication queue.',
      '- **Delete** — destroys the cluster entity. Nodes return to the unassigned pool. Blocked if tenants are still routed.',
    ],
  },

  tenant_migration_recipe: {
    title: 'Migrate tenant data across clusters',
    body: [
      'Generates the SQL recipe for moving ONE tenant\'s historical data from their current cluster to a different cluster. This is the companion to Tenant Routing — routing reassignment only redirects NEW writes, while this recipe handles the existing rows.',
      '',
      '**When to use this:**',
      '- **Noisy-neighbor resolution**: the Data Usage panel shows one tenant at 90%+ of the cluster. Reassign them to a dedicated cluster and then run this to move their history.',
      '- **Compliance-driven placement**: a tenant\'s data must live in a specific region\'s cluster. Reassign + migrate.',
      '- **Decommissioning a cluster**: moving all tenants off before tearing it down.',
      '',
      '**How the recipe works:**',
      '- Uses ClickHouse\'s `remote()` table function — the idiomatic cross-cluster pattern. No external dump/restore tooling needed.',
      '- Step 0 is a reachability sanity check: can the destination cluster actually pull from the source over port 9000? If that fails, fix network / firewall / credentials before running any INSERT step.',
      '- Steps 1..N are one `INSERT INTO dest.<table> SELECT * FROM remote(source...) WHERE org_id = X` per multi-tenant table. Only tables with rows > 0 for this org are included.',
      '- Next-to-last step is a dual-count verification: every `dest_<table>` must equal the corresponding `src_<table>`. If ANY pair differs, DO NOT run the cleanup.',
      '- Last step (optional + destructive) is `ALTER TABLE ... DELETE WHERE org_id=X` on the source cluster. Runs only after verification, and ideally only after a 24 h cooling period.',
      '',
      '**We don\'t auto-execute because:**',
      '1. Large INSERT SELECTs can saturate the destination for hours — operator needs to pick the window.',
      '2. Cross-cluster network reachability is environment-specific and flaky if not tested first.',
      '3. The cleanup DELETE is destructive; operator-in-the-loop judgement needed.',
      '4. The source-cluster password is NOT in the recipe — placeholder is `SOURCE_PASSWORD` so the recipe is safe to paste into Slack, email, or a ticket.',
      '',
      '**Preconditions the tool can\'t check for you:**',
      '- Destination cluster must have enough free disk (see per-table row counts in the recipe preview).',
      '- Destination schema must already have the target tables. If you just created the destination cluster, run Configure Cluster first and let the schema initializer run.',
      '- Tenant routing should already point at the destination — otherwise new writes keep going to the source during your migration window.',
      '',
      '**Duplicate-data window:** between copy and cleanup, both clusters have the tenant\'s rows. New writes only go to the destination (because of routing), so queries from the app see consistent data via the destination. The source\'s copy is effectively dormant until you clean it up.',
      '',
      '**Audited:** every recipe generation logs a `TENANT_MIGRATION_RECIPE_GENERATED` event with source/dest/org/rowCount. Execution is NOT audited — check `system.query_log` on the destination cluster for that.',
    ],
  },

  state_export: {
    title: 'State export bundle',
    body: [
      'Generates a single, canonicalized, SHA-256–signed JSON bundle of the entire operator state and downloads it as a file. Intended for:',
      '- **Compliance audits** — prove what the config looked like on a specific date',
      '- **Incident forensics** — hand a single file to an investigator instead of a login',
      '- **Operator handover** — new on-call gets the full context without needing access yet',
      '- **Disaster recovery** — restore cluster/node/tenant config to a fresh environment from JSON',
      '',
      '**What the bundle includes:**',
      '- Every cluster (topology, connection, backup config)',
      '- Every node (role, slot, resource probe, disk budget)',
      '- Every tenant-cluster assignment',
      '- Every retention policy with last-run status',
      '- Last 30 days of backup jobs',
      '- Last 30 days of operator audit events',
      '- Current WAL stats snapshot',
      '- Current operational-health snapshot',
      '- Current consistency-finding snapshot',
      '',
      '**Secrets are scrubbed** before export: SSH passwords, SSH private keys, DB passwords, S3 secret keys — all replaced with `"***REDACTED***"`. The bundle is safe to share with auditors.',
      '',
      '**Signature:** the bundle carries a `signature` block with SHA-256 of the canonicalized body (map entries sorted by key ascending). An auditor can re-canonicalize with the same rules, re-hash, and verify the bundle hasn\'t been tampered with.',
      '',
      '**When NOT to use this:** operational runtime data (rows in ClickHouse itself). This is ONLY the operator config. For tenant data, use the backup/restore flow.',
      '',
      '**Audit:** every export generates a `STATE_EXPORT_GENERATED` event with the digest prefix — so you can trace which export file matches which audit entry.',
    ],
  },

  schema_migration_recipe: {
    title: 'Schema migration recipe',
    body: [
      'Generates the exact SQL sequence to convert an existing plain `MergeTree` (or `ReplacingMergeTree`, etc.) table into its `Replicated` counterpart. This is what you run AFTER adding replicas to a cluster that was previously single-replica — the new replicas join empty, and existing tables stay non-replicated until you migrate them.',
      '',
      '**The recipe has 5 steps** — we intentionally DO NOT execute them for you. RENAME and DROP are hard-destructive, and the read between steps 4 and 5 is human judgement. Your job as operator:',
      '1. **RENAME** — `RENAME TABLE <t> TO <t>_legacy`. Instant, metadata-only.',
      '2. **CREATE** — creates the new table with `ReplicatedMergeTree(\'/clickhouse/tables/{shard}/<t>\', \'{replica}\')`. The Keeper path uses the same convention as the schema initializer, and `{shard}` / `{replica}` are substituted from `macros.xml` on each node.',
      '3. **COPY** — `INSERT INTO <t> SELECT * FROM <t>_legacy`. Size-proportional duration. ClickHouse replicates via Keeper — only run this on ONE replica; others pick it up automatically.',
      '4. **VERIFY** — a dual-count SELECT. Both counts should match EXACTLY. If they don\'t, stop and investigate before step 5.',
      '5. **DROP** — drops the legacy table. Only after step 4 confirms matching counts; ideally wait 24 h first so you can roll back.',
      '',
      '**Where to run the SQL:** the recipe is copy-paste-ready. You can run it via `clickhouse-client` from a SHH session on a data node, or from any BI tool that can connect to the cluster. The read-only SQL Console in this UI (iteration 11) rejects DDL by design — that\'s what keeps it safe, but it also means you need another path for this one.',
      '',
      '**What we audit:** we log the fact that a recipe was GENERATED (who asked to migrate what). We do NOT audit the execution — you\'ll need to grep `system.query_log` on the cluster itself for that.',
      '',
      '**Caveats:**',
      '- Works for tables in the MergeTree family (MergeTree, ReplacingMergeTree, SummingMergeTree, AggregatingMergeTree). Log/Memory/other engines are not supported.',
      '- If the table already uses `ReplicatedMergeTree`, the endpoint refuses — there\'s nothing to migrate.',
      '- The CREATE DDL is rewritten to the Replicated variant using a regex-based substitution on `ENGINE = <name>(...)`. If our regex misses your engine\'s exact signature, the recipe surfaces a warning line and you adjust by hand.',
      '- Materialized views attached to the legacy table will NOT automatically switch to the new one. Handle them separately.',
    ],
  },

  consistency: {
    title: 'Operator state consistency report',
    body: [
      'A static analysis of the operator\'s own configuration — nodes, clusters, tenant assignments, retention policies — looking for structural drift that accumulates when multiple admins make changes over time. Runs in memory against the MariaDB state; doesn\'t touch ClickHouse itself.',
      '',
      '**Different from Operational Health banner:**',
      '- **Operational Health** asks "is anything broken RIGHT NOW?" (disk full, merges stuck, WAL draining).',
      '- **Consistency report** asks "is the config coherent at all?" (nodes in the wrong shard, tenants routed to a cluster with no connection, retention policies on unknown tables).',
      '',
      '**Checks performed:**',
      '- **TOPOLOGY**: nodes assigned to shardNum outside [1, shardCount]; replica-name collisions within the same shard; clusters RUNNING with no DATA nodes; clusters RUNNING with every DATA node in ERROR/STOPPED (cluster.status stale).',
      '- **REPLICATION**: multi-replica clusters with no KEEPER nodes (→ schema init silently falls back to plain MergeTree); keeper quorum < 3 or even-count.',
      '- **TENANT_ROUTING**: tenant routed to cluster that no longer exists; tenant routed to cluster with no jdbcUrl (writes fall back to default).',
      '- **RETENTION**: retention policy targeting a table not in the schema.',
      '- **VERSION_DRIFT**: DATA nodes in the same cluster on different binary versions (WARNING — rolling-deploy fix); a running node behind the newest uploaded binary (INFO — patch cadence).',
      '',
      '**Each finding includes a suggested fix** so operators don\'t just see "something\'s wrong" but know how to resolve it.',
      '',
      '**When to run:** after any topology change, before declaring a cluster production-ready, and as part of any on-call handover. The report is cheap (just MariaDB reads) so run it often.',
    ],
  },

  operational_health: {
    title: 'Operational health banner',
    body: [
      'A single "is anything wrong right now?" signal at the top of the ClickHouse Infrastructure page. Polls the aggregator endpoint every 30 seconds; expands to show the full issue list on click.',
      '',
      '**What it aggregates:**',
      '- **Cluster status** — any cluster not in RUNNING state (DEGRADED / ERROR / STOPPED / CONFIGURING / PENDING)',
      '- **Node disk pressure** — free space vs budget (if set) or vs total disk, using the same thresholds as the disk-low chip on each node row',
      '- **Node lifecycle** — any node in ERROR state',
      '- **WAL depth** — pending WAL files, dead-lettered files, circuit-breaker state per cluster',
      '- **Retention failures** — policies whose last run failed',
      '- **Backup failures** — backup jobs that failed in the last 24 hours',
      '',
      '**Severities:**',
      '- 🔴 **CRITICAL** — data loss or outage is imminent or happening. E.g. node has <10 GB free, WAL has dead-lettered entries, cluster is ERROR.',
      '- 🟠 **WARNING** — planning attention needed. E.g. disk headroom <20 GB, retention policy last-run failed, cluster DEGRADED (still serving traffic but reduced redundancy).',
      '- 🔵 **INFO** — operations in progress or conditions worth noting but not actionable. E.g. CONFIGURING cluster, HALF_OPEN circuit breaker probing recovery.',
      '',
      'The overall banner severity is the MAX of all issues — so one CRITICAL makes the whole banner red even if the other 5 are INFO. That\'s intentional: when something critical is happening, you shouldn\'t have to scroll through infos to find it.',
      '',
      '**"All checks passing"** (green) is the baseline. If you never see it, the check isn\'t running — reload the page or verify the backend is healthy.',
    ],
  },

  tenant_retention: {
    title: 'Per-tenant retention policies',
    body: [
      'Prune rows for ONE tenant on ONE table to a shorter retention window than the schema-wide TTL. This is how you implement "Free plan = 30 days logs, Enterprise = 730 days metrics" per-tenant retention.',
      '',
      '**Why a policy table and not TTL:**',
      'ClickHouse TTL is a property of the table, not the row — you cannot express "different TTL for different org_ids" in schema DDL. Instead we run a periodic `ALTER TABLE <t> DELETE WHERE org_id = X AND <time_col> < now() - INTERVAL N DAY` mutation. ClickHouse processes the mutation asynchronously in the background, rewriting only the affected parts.',
      '',
      '**Scheduler cadence:** every 30 minutes we scan for due policies. Each policy runs at most once per day (nextRunAt is pushed 24 h forward after each run). First-run happens immediately after you save a new policy.',
      '',
      '**Supported tables** (with the timestamp column used for age):',
      '- `metrics` (timestamp), `logs` (timestamp), `spans` (start_time)',
      '- `rum_events` (timestamp), `rum_sessions` (start_time), `rum_replays` (start_time)',
      '- `service_operations` (last_seen)',
      '',
      '**Dry-run** before saving — the modal has a "How many rows would this delete?" button that runs a COUNT query against the tenant\'s cluster so you know the blast radius before pulling the trigger.',
      '',
      '**Run now** — manual trigger if you can\'t wait for the scheduler (e.g. reacting to a noisy tenant in real time). Audited.',
      '',
      '**What this does NOT do:**',
      '- It cannot EXTEND retention beyond the schema-level TTL (365 days). If you need longer retention for Enterprise tenants, you\'d have to raise the schema TTL and THEN use retention policies to prune Free-plan tenants to a shorter window.',
      '- It doesn\'t touch rows where `org_id = 0` (system / unassigned). Those need to be investigated separately via the Tenant Usage panel.',
      '- It doesn\'t free disk space immediately — ALTER DELETE is async, materialized during background merges. Disk usage drops over hours as parts get rewritten.',
      '',
      '**Audit:** every run (success or failure), policy create, update, delete, and manual Run Now is captured in the Operator Audit Log with full context.',
    ],
  },

  sql_console: {
    title: 'SQL console (read-only)',
    body: [
      'Runs ad-hoc SQL against a cluster\'s leader DATA node. Intended for troubleshooting: answering questions that the Health Detail panels don\'t pre-compute, or inspecting `system.*` tables the UI doesn\'t surface.',
      '',
      '**What is allowed:**',
      '- The statement must start with `SELECT`, `WITH`, `SHOW`, `DESCRIBE`, `DESC`, or `EXPLAIN`.',
      '- Any keyword in the dangerous set is rejected regardless of position: `INSERT`, `UPDATE`, `DELETE`, `ALTER`, `CREATE`, `DROP`, `RENAME`, `SYSTEM`, `ATTACH`, `DETACH`, `OPTIMIZE`, `TRUNCATE`, `GRANT`, `REVOKE`, `KILL`, `EXCHANGE`. This is a coarse but predictable filter — if you need to run those, use the operator UI actions or SSH.',
      '',
      '**Safety settings we always append:**',
      '- `max_result_rows = 1000` — result capped; the UI shows a "truncated" chip when this fires.',
      '- `result_overflow_mode = break` — ClickHouse stops sending rows instead of erroring on overflow.',
      '- `max_execution_time = 30` seconds — a runaway query cannot hang the server.',
      '- `readonly = 1` — belt-and-braces. Even a SELECT that tries to mutate via side-effect functions gets blocked by ClickHouse itself.',
      '',
      '**Workflow:**',
      '- Pick a cluster, pick a snippet (or paste your own SQL), **Run** (Ctrl/Cmd + Enter).',
      '- Result grid is click-to-copy per cell.',
      '- **Download CSV** grabs the current result set for paste into a spreadsheet / ticket.',
      '',
      '**Audited:** every execution is recorded in the Operator Audit Log with action `CONSOLE_QUERY`, the first 200 chars of the SQL, row count, elapsed ms, and the authenticated actor. Failures (policy violations, CH errors) are audited too — useful for spotting someone probing the limits.',
      '',
      '**When NOT to use this:** anything mutation-like. The console will refuse, but beyond that: if you\'re manually running the same query twice a week, turn it into a first-class UI feature or a scheduled job instead.',
    ],
  },

  audit_log: {
    title: 'Operator audit log',
    body: [
      'A merged, read-only timeline of every operator action that changed cluster, node, tenant, backup, connection, or disk-budget state. Use it for incident forensics ("who restarted the cluster at 02:13?"), change review, and compliance reporting.',
      '',
      '**Sources merged into one feed:**',
      '- **operator** — cluster create/delete/configure/deploy/start/stop/graceful-stop, tenant assign/unassign, connection edits, disk-budget set/clear. Written to MariaDB `operator_audit_events`.',
      '- **deployment** — per-node deploy/start/stop/restart/preflight steps. Sourced from the existing `deployment_logs` table.',
      '- **backup** — clickhouse-backup create/upload/restore/delete runs. Sourced from `clickhouse_backup_jobs`.',
      '',
      '**Filters:**',
      '- **Window** — time range to pull. Default 7 days. Longer windows are slower because the `deployment_logs` and `clickhouse_backup_jobs` queries don\'t filter server-side on timestamp.',
      '- **Source** — chip filter. Useful for narrowing to only backup runs or only deployment activity.',
      '- **Actor contains** — substring match on the user who initiated the action (`admin`, `scheduler`, `system`).',
      '- **Action contains** — substring match on action name (`STOP`, `DELETE`, `BUDGET_SET`).',
      '',
      '**What the icons mean:**',
      '- ✅ green check = action succeeded',
      '- ❌ red × = action failed; hover for the error message',
      '- The "failures" chip top-right shows the count of ❌ in the current window — if it\'s non-zero, investigate.',
      '',
      '**What is NOT in this log** (yet):',
      '- Read-only operations (health checks, insight probes, list endpoints).',
      '- Tenant-level data writes.',
      '- Automatic WAL replays — surfaced on the Backups tab instead.',
      '',
      '**Retention:** rows persist in MariaDB indefinitely. For compliance archival, export via `mysqldump operator_audit_events deployment_logs clickhouse_backup_jobs`.',
    ],
  },

  tenant_usage: {
    title: 'Data usage by tenant',
    body: [
      'Shows how much data each organization has on the selected cluster, across every multi-tenant table (`metrics`, `spans`, `logs`, `rum_*`, `service_operations`). Used for cost-allocation, noisy-neighbor detection, and retention decisions.',
      '',
      '**How the numbers are computed:**',
      '- **Rows** — exact. `SELECT org_id, count() FROM <table> GROUP BY org_id` against each table on the cluster leader.',
      '- **Est. size** — an estimate. ClickHouse doesn\'t track per-row disk bytes natively, so for each table we take `system.parts.bytes_on_disk` and pro-rate it by the tenant\'s fraction of rows. Compression ratios do vary slightly by tenant (depending on the cardinality of their label values), so expect ±10% noise on heavy-cardinality workloads.',
      '',
      '**What to do with this:**',
      '- One tenant at 80% of the cluster? Consider a dedicated cluster for them via Tenant Routing.',
      '- Rows attributed to **`org_id=0` / "system/unassigned"**? That\'s writes that bypassed tenant context — either system-level or a bug. Investigate before the volume grows.',
      '- Consistent growth of a single tenant over time? Surface to Sales — may warrant a retention review or plan upgrade.',
      '',
      '**Performance:** the probe hits `system.parts` (cheap) plus one `GROUP BY org_id` per table. On a 40 M-row cluster it takes roughly 1 second per table. For very large clusters this can be a few seconds — acceptable as an on-demand operator tool, not something to poll continuously.',
    ],
  },

  cluster_health_detail: {
    title: 'Cluster health detail',
    body: [
      'Read-only operational deep-dive into a running cluster. Powered by `system.*` table queries against the cluster leader; data is current at query time, not cached.',
      '',
      '**Panels:**',
      '- **Disk usage** — bytes + rows + active-part count per table, descending by size. Use this to find runaway tenants or tables that need TTL tuning.',
      '- **Active merges** — merges currently in flight. Numbers near `progress=1.0` are about to finish. A merge stuck at low progress for > 10 min on a small table usually means a quorum or disk issue.',
      '- **Slow queries** — the top 10 longest-running `SELECT`s in the last hour from `system.query_log`. If the panel is empty, either no slow queries ran OR `query_log` is disabled in the CH config.',
      '- **Mutations** — in-flight `ALTER UPDATE` / `ALTER DELETE`. Stuck mutations block subsequent DDL; check `latest_fail_reason`.',
      '- **Replication queue** — pending replication tasks. Meaningful only on ReplicatedMergeTree clusters. `num_tries > 0` with a non-null `last_exception` = stuck replication.',
      '',
      '**What to do with this data:**',
      '- Disk % getting scary → either tune TTL per table (deferred iteration) or scale out a new shard.',
      '- Slow queries dominated by one tenant → consider a dedicated cluster for them (Tenant Routing).',
      '- Stuck mutation → `KILL MUTATION WHERE mutation_id = \'…\'` via a direct CH session.',
      '- Growing replication queue → check keeper quorum, network between replicas, disk headroom on the target replica.',
    ],
  },

  wal_durability: {
    title: 'Write durability (WAL + circuit breaker)',
    body: [
      'This tile shows the health of the **write-ahead log** (WAL) that protects ClickHouse ingestion against transient failures and crashes.',
      '',
      '**How the durability layer works:**',
      '- Every flush from the in-memory batch buffer is routed to the correct per-tenant cluster.',
      '- If a flush **fails** (network blip, ClickHouse down, disk full, auth error), the batch is serialized to a `.wal` file under `./data/ch-wal/<clusterId>/<table>/` so it cannot be lost.',
      '- A **circuit breaker** per cluster opens after 5 consecutive failures — subsequent batches skip the normal flush and go straight to WAL, reducing pressure on a sick cluster.',
      '- A replay loop runs every second, picks the oldest due WAL files, and retries with exponential backoff (1 s → 60 s).',
      '- After 10 failed retries a file is moved to the **dead-letter** directory for operator inspection.',
      '',
      '**What to watch:**',
      '- `Pending WAL files` — normally 0. Growing = downstream ClickHouse has issues.',
      '- `Dead-lettered` — any value > 0 indicates permanently-failed batches you should investigate in `./data/ch-wal/dead/` on the backend host.',
      '- `Per-cluster circuit` — `CLOSED` is healthy, `HALF_OPEN` means we\'re probing recovery, `OPEN` means we\'re shedding load to the WAL.',
      '',
      'The tile auto-refreshes every 10 seconds.',
    ],
  },

  backups_tab: {
    title: 'Backups',
    body: [
      'Create and restore **cluster-level snapshots** of your ClickHouse data using the open-source `clickhouse-backup` tool. One snapshot can cover every tenant on the cluster.',
      '',
      '**Typical workflow:**',
      '1. **Install backup tool** — SSHes into every DATA node and installs the `clickhouse-backup` binary + a `config.yml`.',
      '2. Configure **Schedule & Storage** (cron + retention, optional S3 destination) and **Save**.',
      '3. **Run backup now** to verify end-to-end, or wait for the cron to fire.',
      '4. **Restore** any listed backup back onto the same cluster in case of data loss or corruption.',
      '',
      'Backups are per-cluster (not per-tenant). A single snapshot contains the whole `verops` database with every tenant\'s rows — this is a platform-operator responsibility, not a per-tenant feature.',
      '',
      '**⚠ Restore is destructive** — it overwrites existing data in the same tables. Protect it with the cluster-name confirmation text.',
      '',
      '**S3 is optional** but strongly recommended for production. Local backups share the same disk as ClickHouse itself, so a disk failure would take both with it.',
    ],
  },

  install_backup_tool: {
    title: 'Install backup tool',
    body: [
      'SSHes into every DATA node in the cluster and installs:',
      '- `/usr/local/bin/clickhouse-backup` — the Altinity clickhouse-backup binary (`v2.6.2`)',
      '- `/etc/clickhouse-backup/config.yml` — generated from the Schedule & Storage fields',
      '',
      'Idempotent: if the binary is already present at the correct version, it skips the download. The config file is rewritten every time so S3 changes take effect without re-downloading.',
      '',
      'Run this once when you create a cluster, and again any time you change S3 credentials or the ClickHouse DB password.',
    ],
  },

  run_backup_now: {
    title: 'Run backup now',
    body: [
      'Triggers an immediate snapshot with a name like `verops_20260424_153042`. The run is synchronous — the button stays disabled until it finishes.',
      '',
      '**What it does on the leader DATA node:**',
      '1. `clickhouse-backup create --tables \'<database>.*\' <name>` — uses ClickHouse\'s native FREEZE under the hood (hard links, doesn\'t duplicate data; near-instant for small tables).',
      '2. If S3 is configured: `clickhouse-backup upload <name>`.',
      '3. Records the job in the database for the Recent Jobs table.',
      '',
      '**When it blocks writes:** it doesn\'t. `create` is a metadata + hardlink operation; writes continue unaffected.',
    ],
  },

  backup_schedule: {
    title: 'Schedule & Storage',
    body: [
      '**Enable scheduled backups** to let the backend fire this backup on a cron.',
      '',
      '**Cron (6-field)** — Spring cron format: `seconds minutes hours day-of-month month day-of-week`.',
      '- `0 0 2 * * *` — every day at 02:00',
      '- `0 0 */6 * * *` — every 6 hours',
      '- `0 30 1 * * SUN` — Sundays at 01:30',
      '',
      '**Retention (days)** — `clickhouse-backup` prunes older-than-N backups on each run. Set to 0 to keep forever (dangerous — disk fills up).',
      '',
      '**Missed schedules during downtime** — if the backend was down when the cron would have fired, we skip that firing. Backups are additive, so a missed one is not a data-loss event, but your snapshot cadence drops temporarily.',
    ],
  },

  backup_s3: {
    title: 'S3 remote storage',
    body: [
      'Recommended for production. Without S3, backups live on the SAME DISK as ClickHouse — a disk failure takes both.',
      '',
      '**Supported:** AWS S3, any S3-compatible store (MinIO, Wasabi, B2, Cloudflare R2, Scaleway, Ceph RGW, …).',
      '',
      '**Fields:**',
      '- `Bucket` — required. Must exist; we don\'t create it.',
      '- `Region` — required for AWS. For MinIO set to any value (e.g. `us-east-1`).',
      '- `Endpoint` — leave blank for AWS. For MinIO/other: the full URL (e.g. `https://minio.example.com:9000`). When set, `force_path_style` is enabled automatically.',
      '- `Access key` / `Secret key` — the IAM user or access-key credentials with `s3:PutObject`, `s3:GetObject`, `s3:DeleteObject`, `s3:ListBucket` on the bucket.',
      '- `Prefix` — optional path inside the bucket (e.g. `backups/prod`). Lets one bucket hold backups from multiple clusters.',
      '',
      '**Security:** the secret key is stored plaintext in the operator DB today. TODO: integrate with a secrets manager. Restrict access to the operator tenant.',
    ],
  },

  backups_list: {
    title: 'Available backups',
    body: [
      '"Query backups" runs `clickhouse-backup list` on the cluster leader. It returns both local (`/var/lib/clickhouse/backup/<name>`) and remote (S3) backups.',
      '',
      'Local and remote are shown as separate rows even for the same `name` — you may see e.g. `verops_20260424_020000` twice: once with `local` and once with `remote` location chips. That\'s normal. You can restore from either.',
      '',
      'Backups are NOT shown in this list if they were created by another tool (vanilla `BACKUP TABLE ... TO DISK`, manual `rsync`, …). This UI only understands `clickhouse-backup` output.',
    ],
  },

  backup_jobs: {
    title: 'Recent jobs',
    body: [
      'Shows the last ~25 backup-related operations performed by this platform (CREATE / RESTORE / DELETE). Entries are persisted in MariaDB, so they survive backend restarts.',
      '',
      '**Status values:**',
      '- `PENDING` — queued, not yet started',
      '- `RUNNING` — currently executing on the cluster leader',
      '- `SUCCESS` — finished cleanly',
      '- `FAILED` — see the Error column',
      '',
      'A job stuck in `RUNNING` for longer than 2 hours is auto-marked as `FAILED` by the reaper (covers the case where the backend died mid-run).',
    ],
  },

  restore_backup: {
    title: 'Restore backup',
    body: [
      '**Destructive.** Replaces data in the same tables on the target cluster with the contents of the backup.',
      '',
      '**What happens:**',
      '1. (If "Download from S3 first" is on) `clickhouse-backup download <name>` — pulls the backup from remote into local storage.',
      '2. `clickhouse-backup restore <name>` — restores each table. Existing rows in the same partitions are replaced; partitions not in the backup are untouched.',
      '',
      '**Write traffic during restore:** clickhouse-backup restores partition-by-partition. Queries and writes against unrelated partitions keep working. Writes against the SAME partition may fail or land after the restore overwrites them — schedule restores during a maintenance window when possible.',
      '',
      '**Cross-cluster restore is NOT supported in V1.** The restore always targets the same cluster the backup was taken from.',
    ],
  },

  replication_upgrade: {
    title: 'Replication (ReplicatedMergeTree)',
    body: [
      'Clusters with `replicasPerShard > 1` get **ReplicatedMergeTree** tables automatically on schema init. Keeper coordinates replication, and data written to any replica is copied to every other replica in the shard. A disk or node loss no longer loses data.',
      '',
      '**How VerOps decides:** during schema init we probe `system.macros` on the target cluster. If the `replica` macro is present (emitted by the cluster configurator into `macros.xml` whenever the topology has more than one replica), tables are created as `ReplicatedMergeTree(/clickhouse/tables/{shard}/<table>, {replica})`. Otherwise we fall back to plain `MergeTree`.',
      '',
      '**Upgrading an existing single-replica cluster:**',
      '1. Add replicas to the topology and **Configure Cluster** so `macros.xml` on every node now includes `{replica}` and `use_keeper.xml` is populated.',
      '2. Restart all nodes so ClickHouse picks up the new config.',
      '3. Existing tables stay `MergeTree` — **CREATE TABLE IF NOT EXISTS is a no-op once the table exists, so changing engine family requires a manual migration.**',
      '4. Per-table migration (operator runs by hand against the CH cluster):',
      '   - `RENAME TABLE <t> TO <t>_legacy`',
      '   - Run schema init — creates `<t>` as `ReplicatedMergeTree`',
      '   - `INSERT INTO <t> SELECT * FROM <t>_legacy`',
      '   - Verify row counts match',
      '   - `DROP TABLE <t>_legacy`',
      '',
      '⚠ This migration is destructive and invalidates ongoing writes during steps 1–4. Run during a maintenance window. Writes that land during the window go to the WAL (iteration 2 protects them), but reads of the in-flight table will miss until the swap completes.',
    ],
  },

  tenant_routing_actions_legend: {
    title: 'Tenant-routing actions — what each icon does',
    body: [
      '- **Configure Connection** — set `jdbcUrl` / `dbUsername` / `dbPassword` / `databaseName` for a cluster. Required before tenants can be assigned.',
      '- **Test Connection** — opens a JDBC connection and runs `SELECT 1 + 1`. Use to verify connectivity after changing the fields.',
      '- **Assign Tenant** — pin an organization to a cluster. Runs schema init on the target cluster.',
      '- **Unassign Tenant** — remove the pinning. Tenant falls back to the default cluster (rarely desired — re-assign directly to a different cluster instead).',
    ],
  },

  // ─────────────────────── Clusters tab ───────────────────────
  clusters_tab: {
    title: 'Clusters',
    body: [
      'A **cluster** is a named group of nodes arranged in a shard/replica topology. Each tenant (organization) is routed to exactly one cluster for all reads and writes.',
      '',
      '**Topology choices:**',
      '- **Single-Node** — 1 shard × 1 replica. Good for dev/staging. Zero HA, disk loss = data loss.',
      '- **Single-Shard Replicated** — 1 shard × N replicas (N = 2 or 3). Full data copies on every node. HA, no horizontal scaling.',
      '- **Multi-Shard (Sharded)** — K shards × N replicas each. Horizontal scaling; needs Keeper.',
      '',
      '**Keeper**: required whenever `replicasPerShard > 1`. Provide an odd number of keeper nodes (3 or 5) in a separate host pool for production. We deploy keeper on the same cluster nodes if you don\'t nominate a dedicated set.',
    ],
  },

  create_cluster: {
    title: 'Create Cluster',
    body: [
      'Defines a new cluster in the orchestrator DB. No nodes are deployed yet.',
      '',
      '**After you create:**',
      '1. **Assign Node** to each shard/replica slot (you\'ll need `shard_count × replicas_per_shard` DATA nodes plus keeper nodes if multi-replica).',
      '2. **Deploy Cluster Binary** to push ClickHouse to every assigned node in parallel.',
      '3. **Start Cluster** — keeper nodes first, then DATA nodes in order.',
      '4. Run **Cluster Health Check** to verify all nodes joined the cluster.',
      '',
      '**Database name** defaults to `verops` — keep it unless you really need a separate schema. All tenant tables are created under this database.',
      '',
      '**Connection fields** (`jdbcUrl`, `dbUsername`, `dbPassword`) are how the backend\'s query path reaches this cluster. If left blank, we auto-derive from the first DATA node.',
      '',
      '**Replication** — if you pick a topology with `replicasPerShard > 1`, the backend schema initializer probes `system.macros` on the cluster and creates tables as `ReplicatedMergeTree` automatically. Keeper coordinates replication. See the **Replication** help entry for upgrade paths on existing single-replica clusters.',
    ],
  },

  assign_node_to_cluster: {
    title: 'Assign Node to Cluster',
    body: [
      'Places an existing node into a specific slot in the cluster topology.',
      '',
      '**You pick:**',
      '- `shardNum` — 1..N where N = cluster `shard_count`',
      '- `replicaName` — any unique string within the shard (conventionally `replica_1`, `replica_2`, …)',
      '- `nodeRole` — `DATA` or `KEEPER`',
      '',
      '⚠ A node already assigned to another cluster cannot be reassigned without first unassigning. If you move a DATA node to a new cluster, its local data is NOT migrated — treat it as a fresh node.',
    ],
  },

  configure_cluster: {
    title: 'Configure Cluster',
    body: [
      'Regenerates and pushes the cluster config XML files to every assigned node over SSH:',
      '- `remote_servers.xml` — the cluster name + shard/replica list',
      '- `macros.xml` — per-node `{shard}` and `{replica}` substitutions',
      '- `use_keeper.xml` — keeper endpoints (if multi-replica)',
      '- `keeper.xml` — Raft config on each KEEPER node',
      '',
      'A restart of each affected node is required for the new config to take effect. The orchestrator does NOT auto-restart — you run **Start/Restart** explicitly so you control the outage window.',
      '',
      'Use this any time you change the topology (add a shard, add a replica, rotate a keeper).',
    ],
  },

  deploy_cluster_binary: {
    title: 'Deploy Cluster Binary',
    body: [
      'Runs **Deploy Binary** on every node in the cluster in parallel. Keeper nodes finish first (they stop accepting writes via the same flow as DATA nodes, so ordering matters for bootstrap).',
      '',
      '⚠ All nodes restart. For active clusters, this IS a cluster-wide outage of up to a few minutes. Coming: zero-downtime rolling upgrade.',
    ],
  },

  start_cluster: {
    title: 'Start Cluster',
    body: [
      'Brings every node in the cluster up in the correct order:',
      '1. **KEEPER nodes** first — 5-second pause to form quorum',
      '2. **DATA nodes** next, in shard/replica order',
      '',
      'Health check polls each node after start until it answers `SELECT 1`.',
    ],
  },

  stop_cluster: {
    title: 'Stop Cluster',
    body: [
      'Stops every node in the cluster:',
      '1. **DATA nodes** first (so writes land in a quorum-backed keeper before keeper shuts down)',
      '2. **KEEPER nodes** last',
      '',
      '⚠ This is NOT a drain — active writes may be lost. The **Graceful Drain** action (coming in iteration 5) sets the cluster read-only, waits for in-flight writes to flush, then stops. Use that path for zero-loss maintenance.',
    ],
  },

  cluster_health: {
    title: 'Cluster Health Check',
    body: [
      'Aggregates **Health Check** results across every node in the cluster and returns a summary verdict.',
      '',
      '**Verdict:**',
      '- **RUNNING** — every node answered `SELECT 1`',
      '- **DEGRADED** — at least one node is offline or reporting issues, but the cluster still has enough replicas to serve reads/writes',
      '- **ERROR** — cluster cannot serve traffic (e.g., all replicas of a shard down)',
    ],
  },

  delete_cluster: {
    title: 'Delete Cluster',
    body: [
      '**Destructive.** Removes the cluster entity and unassigns every node from it. Nodes themselves are NOT deleted — they return to the "unassigned" pool.',
      '',
      '**Blocked if any tenant is still routed to this cluster.** Go to **Tenant Routing** and move or remove every tenant assignment first.',
      '',
      'Data on the underlying nodes is left untouched. You can wire the same nodes into a new cluster later if needed.',
    ],
  },

  view_cluster_nodes: {
    title: 'View Cluster Nodes',
    body: [
      'Opens a read-only drill-down of every node assigned to this cluster. Two views in one dialog:',
      '',
      '1. **Topology diagram** (top) — a visual tree of the cluster\'s physical layout. DATA nodes are grouped into shards; KEEPER nodes live in a separate row. Each replica card shows the node\'s status chip (green=RUNNING, red=ERROR, etc.), host:port, shard/replica label, ClickHouse version, and disk usage-vs-budget.',
      '   - Empty slots (when declared `replicasPerShard` exceeds the number of assigned replicas) show as dashed cards so topology gaps are obvious.',
      '   - The keeper row surfaces quorum warnings: fewer than 3 keepers = not fault-tolerant; even count = no extra fault tolerance over (N-1).',
      '   - If the cluster declares replicas > 1 but has NO keeper nodes, a warning alert appears — schema init would fall back to plain MergeTree without keeper.',
      '2. **Table listing** (below) — the canonical row-per-node view with Remove-from-cluster action.',
      '',
      'Use this when you\'re about to do a cluster-wide operation and want to see the membership at a glance, OR when planning a topology change and need to see where the holes are.',
    ],
  },

  // ─────────────────────── Tenant Routing tab ───────────────────────
  tenant_routing_tab: {
    title: 'Tenant Routing',
    body: [
      'Controls which ClickHouse cluster each organization\'s data lives in. The backend reads this table on every query and on every write (buffered batches route per-org).',
      '',
      '**Model:** one-to-one — each organization can be routed to at most one cluster at a time. Unassigned organizations fall back to the platform\'s default cluster (configured in `application.properties`).',
      '',
      '**When to assign:**',
      '- Large tenant needs a dedicated cluster for isolation',
      '- Compliance requirement (data residency)',
      '- Noisy-neighbor mitigation',
      '',
      '**⚠ Data implications of re-routing:** if you re-assign a tenant from cluster A → cluster B, their historical data STAYS on A unless you migrate it. New writes land on B. Reads look at B only. Migration tooling is coming in iteration 4.',
    ],
  },

  assign_tenant: {
    title: 'Assign Tenant to Cluster',
    body: [
      'Pins an organization to a specific cluster. All subsequent reads/writes for that org go to that cluster.',
      '',
      '**Steps the backend takes on save:**',
      '1. Upsert the row in `tenant_cluster_assignments`.',
      '2. Invalidate the resolver cache (every app instance sees the change on next query).',
      '3. Run `ClickHouseSchemaInitializer.initializeSchemaOn()` on the target cluster so all required tables exist.',
      '',
      '⚠ If the target cluster doesn\'t have the schema, step 3 creates it. This is safe but may take a few seconds on a brand-new cluster.',
      '',
      '⚠ **Historical data for this tenant on the old cluster stays there.** You need a manual data migration if you want a clean cutover.',
    ],
  },

  unassign_tenant: {
    title: 'Unassign Tenant',
    body: [
      '**Destructive to the live path.** Immediately after unassign:',
      '- The tenant\'s next write goes to the DEFAULT cluster.',
      '- Reads also fall back to the default cluster.',
      '- Any data written since the original assignment now lives on a cluster the tenant is no longer reading from.',
      '',
      'This is rarely the right action. If you want to move a tenant to a different cluster, **Assign Tenant** to the new cluster directly instead (the assignment replaces the old one atomically).',
    ],
  },

  configure_cluster_connection: {
    title: 'Configure Cluster Connection',
    body: [
      'Edits how the backend reaches this cluster:',
      '- `jdbcUrl` — e.g., `jdbc:ch://<host>:<http_port>/verops`',
      '- `dbUsername` / `dbPassword` — ClickHouse SQL user credentials',
      '- `databaseName` — database name on the cluster (defaults to `verops`)',
      '',
      'The backend caches this per-cluster in a connection pool (HikariCP, 10 connections). On save, the cache is invalidated and the next query builds a fresh pool.',
      '',
      'Leave blank and the orchestrator auto-derives from the first DATA node\'s host + HTTP port on deploy. Override only if you have a load balancer in front or custom auth.',
    ],
  },

  test_cluster_connection: {
    title: 'Test Cluster Connection',
    body: [
      'Opens a JDBC connection using the configured `jdbcUrl` + credentials and runs `SELECT 1 + 1`. Reports success/failure with the error message on failure.',
      '',
      'Use this after changing connection fields to verify the backend can actually reach the cluster before pointing tenant traffic at it.',
    ],
  },
};

/**
 * Backward-compat: allow referencing docs by arbitrary key without crashing
 * if a key is missing. Shows a sensible placeholder so stray <HelpIcon/>
 * never renders a blank modal.
 */
export function getHelpDoc(key) {
  return (
    CLICKHOUSE_HELP[key] || {
      title: 'Help',
      body: ['No documentation available for this feature yet.'],
    }
  );
}
